/* eslint-disable react-hooks/immutability */
import { faTrashXmark } from "@fortawesome/pro-regular-svg-icons";
import { faClose, faSave, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, FormControlLabel, IconButton, Stack, Switch, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm } from "../../../../components/FormProvider";
import { CommunicationRecipient } from "../../../../models";

export function EditContactForm(props: {
  familyId: number;
  contact: CommunicationRecipient | null;
  resetFormRef: React.RefObject<(() => void) | null>;
  dataGetterRef: React.RefObject<(() => CommunicationRecipient | null) | null>;
}) {
  const { familyId, contact } = props;
  const form = useForm();

  const [isEmailOptedIn, setIsEmailOptedIn] = useState<boolean>(contact?.isEmailOptedIn ?? false);
  const [isSMSOptedIn, setIsSMSOptedIn] = useState<boolean>(contact?.isSMSOptedIn ?? false);

  // use refs for the text fields to improve performance. otherwise every character entered/deleted causes a re-render, making it feel very slow
  // it's not as noticable with the other types of inputs, so we don't need to make those ref based.
  const nameField = useRef<HTMLInputElement | null>(null);
  const phoneField = useRef<HTMLInputElement | null>(null);
  const emailField = useRef<HTMLInputElement | null>(null);

  const resetForm = (): void => {
    if (nameField.current) {
      nameField.current.value = "";
    }
    if (phoneField.current) {
      phoneField.current.value = "";
    }
    if (emailField.current) {
      emailField.current.value = "";
    }
    setIsEmailOptedIn(contact?.isEmailOptedIn ?? false);
    setIsSMSOptedIn(contact?.isSMSOptedIn ?? false);
    if (nameField.current) {
      nameField.current.value = contact?.name ?? "";
    }
  };

  const getUpdatedRecord = (): CommunicationRecipient => {
    return {
      id: contact?.id ?? 0,
      familyId: contact?.familyId ?? familyId,
      phoneNumber: phoneField?.current?.value ?? "",
      email: emailField?.current?.value ?? "",
      isSMSOptedIn,
      isEmailOptedIn,
      deleted: false,
      name: nameField?.current?.value ?? "",
      createdCentralDateTime: "2000-01-01", // doesn't matter what it is, it will be set by the api
      modifiedCentralDateTime: "2000-01-01", // doesn't matter what it is, it will be set by the api
    };
  };

  useEffect(() => {
    if (props.dataGetterRef) {
      props.dataGetterRef.current = getUpdatedRecord;
    }
    if (props.resetFormRef) {
      props.resetFormRef.current = resetForm;
    }
  });

  const handleSMSChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSMSOptedIn(event.target.checked);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsEmailOptedIn(event.target.checked);
  };

  return (
    <Stack gap={2} flexGrow={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Add Contact
        </Typography>
        <IconButton sx={{ width: 44, height: 44 }} onClick={form.cancel}>
          <FontAwesomeIcon icon={faClose} />
        </IconButton>
      </Stack>

      <Stack sx={{ alignItems: { xs: "center", md: "flex-start" } }} flexGrow={1}>
        <Stack gap={1} flexGrow={1} width="100%">
          <TextField variant="filled" label="Name" defaultValue={contact?.name ?? ""} inputRef={nameField} disabled={form.isDisabled} />
          <TextField variant="filled" label="Phone" defaultValue={contact?.phoneNumber ?? ""} inputRef={phoneField} disabled={form.isDisabled} />
          <TextField variant="filled" label="Email" defaultValue={contact?.email ?? ""} inputRef={emailField} disabled={form.isDisabled} />
          <FormControlLabel
            control={<Switch checked={isEmailOptedIn} onChange={handleEmailChange} disabled={form.isDisabled} />}
            label={isEmailOptedIn ? "Emails On" : "Emails Off"}
          />
          <FormControlLabel
            control={<Switch checked={isSMSOptedIn} onChange={handleSMSChange} disabled={form.isDisabled} />}
            label={isSMSOptedIn ? "SMS On" : "SMS Off"}
          />
        </Stack>
      </Stack>

      <Button variant="contained" onClick={form.save} sx={{ height: 50, width: { xs: "100%", md: "inherit" } }} startIcon={<FontAwesomeIcon icon={faSave} />}>
        Save
      </Button>

      {!contact && (
        <Button
          variant="outlined"
          color="error"
          onClick={form.cancel}
          sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
          startIcon={<FontAwesomeIcon icon={faTimes} />}
        >
          Cancel
        </Button>
      )}

      {contact && (
        <Button
          variant="outlined"
          color="error"
          onClick={form.delete}
          sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
          startIcon={<FontAwesomeIcon icon={faTrashXmark} />}
        >
          Delete
        </Button>
      )}
    </Stack>
  );
}
